import { Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ExternalLink, Clock, XCircle } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'

interface Execution {
  id: number
  external_execution_id: string
  workflow_id: number
  workflow_name: string
  client_name: string
  instance_name: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  error_category: string | null
  error_category_label: string
  error_message: string | null
  error_payload: Record<string, unknown> | null
  n8n_execution_url: string
}

interface RelatedFailure {
  id: number
  started_at: string | null
  error_category: string | null
  error_message: string | null
}

interface Props {
  execution: Execution
  related_failures: RelatedFailure[]
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString()
}

export default function ErrorShow({ execution, related_failures }: Props) {
  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/errors">
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Error Details</h1>
              <p className="text-muted-foreground font-mono">
                Execution {execution.external_execution_id}
              </p>
            </div>
          </div>
          <Button variant="outline" asChild>
            <a href={execution.n8n_execution_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              View in n8n
            </a>
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Workflow</CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                href={`/workflows/${execution.workflow_id}`}
                className="font-medium hover:underline"
              >
                {execution.workflow_name}
              </Link>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Client / Instance</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{execution.client_name}</p>
              <p className="text-sm text-muted-foreground">{execution.instance_name}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Started At</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{formatDate(execution.started_at)}</p>
              {execution.duration_seconds && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {execution.duration_seconds}s
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Category</CardDescription>
            </CardHeader>
            <CardContent>
              <Badge variant="outline">{execution.error_category_label}</Badge>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Error Message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-destructive/10 p-4">
              <pre className="whitespace-pre-wrap text-sm text-destructive font-mono">
                {execution.error_message || 'No error message available'}
              </pre>
            </div>
          </CardContent>
        </Card>

        {execution.error_payload && (
          <Card>
            <CardHeader>
              <CardTitle>Error Payload</CardTitle>
              <CardDescription>
                Sanitized error details from n8n (sensitive data redacted).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] rounded-lg border">
                <pre className="p-4 text-sm font-mono">
                  {JSON.stringify(execution.error_payload, null, 2)}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Related Failures</CardTitle>
            <CardDescription>
              Other recent failures from the same workflow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {related_failures.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No other recent failures for this workflow.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {related_failures.map((failure) => (
                    <TableRow key={failure.id}>
                      <TableCell>{formatDate(failure.started_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{failure.error_category || 'Unknown'}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {failure.error_message || '-'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link href={`/errors/${failure.id}`}>View</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
